/*
 * CDK mobile checkout-return guard (unpublished theme).
 *
 * An Android/Shop Pay Back navigation can restore a hidden native cart
 * dialog which still holds the scroll lock. The first cart-icon tap then
 * closes that ghost dialog, which explains the observed double-tap.
 *
 * Reset the already-open drawer state when returning from checkout rather
 * than reloading the storefront or altering Shopify's checkout.
 */
(() => {
  'use strict';
  if (!window.matchMedia('(max-width: 989px)').matches) return;

  const key = 'cdk-mobile-checkout-return-pending-v2';
  const maxAgeMs = 30 * 60 * 1000;
  let recovering = false;

  function markCheckout() {
    try {
      sessionStorage.setItem(key, String(Date.now()));
    } catch (_) {
      // Storage restrictions must never interfere with checkout.
    }
  }

  function clearCheckoutMarker() {
    try {
      sessionStorage.removeItem(key);
    } catch (_) {}
  }

  function isCheckoutReturnPending() {
    try {
      const timestamp = Number(sessionStorage.getItem(key) || 0);
      if (timestamp && Date.now() >= timestamp && Date.now() - timestamp <= maxAgeMs) {
        return true;
      }
    } catch (_) {}
    clearCheckoutMarker();
    return false;
  }

  function recoverCheckoutReturn() {
    if (recovering || !isCheckoutReturnPending()) return;
    recovering = true;

    // Wait for the cart-drawer Web Component's public recovery method.
    // No page refresh, no form submission, and no changes to cart contents.
    customElements.whenDefined('theme-drawer')
      .then(() => {
        const cartDrawer = document.getElementById('cart-drawer');
        if (cartDrawer && typeof cartDrawer.resetAfterCheckoutReturn === 'function') {
          cartDrawer.resetAfterCheckoutReturn();
          clearCheckoutMarker();
        } else if (!cartDrawer) {
          // Cart-page template has no cart drawer.
          clearCheckoutMarker();
        }
      })
      .catch(() => {})
      .finally(() => {
        recovering = false;
      });
  }

  // CDK draft-only mobile fallback: route the header cart icon straight to
  // the full cart page rather than opening a modal that can freeze Chrome.
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element
      ? event.target.closest('[data-testid="cart-drawer-trigger"]')
      : null;
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const cartUrl = new URL('/cart', window.location.origin);
    cartUrl.searchParams.set('preview_theme_id', '188450046178');
    window.location.assign(cartUrl.toString());
  }, true);

  function isCheckoutButton(target) {
    return target instanceof Element &&
      Boolean(target.closest('button#checkout, button[name="checkout"], .cart__checkout-button'));
  }

  document.addEventListener('click', (event) => {
    if (isCheckoutButton(event.target)) markCheckout();
  }, true);

  document.addEventListener('submit', (event) => {
    if (!isCheckoutButton(event.submitter)) return;

    markCheckout();

    // The checkout button submits a form that lives inside the mobile cart
    // modal. Chrome can snapshot that modal during external checkout, leaving
    // the returned storefront visually frozen despite ordinary scroll values.
    // Close the native top-layer dialog BEFORE the browser starts navigation.
    // The form and submitter remain in the DOM; do not cancel the submit event.
    const drawer = event.submitter.closest('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.resetAfterCheckoutReturn === 'function') {
      drawer.resetAfterCheckoutReturn();
    }
  }, true);

  // Handle both browser cache restores and same-document history returns.
  // Chrome's pageshow event may have persisted=false even after checkout.
  window.addEventListener('pageshow', recoverCheckoutReturn);
  window.addEventListener('popstate', recoverCheckoutReturn);
  window.addEventListener('focus', recoverCheckoutReturn);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) recoverCheckoutReturn();
  });

  // If this script is evaluated after the restore event, still recover.
  if (document.readyState === 'complete') recoverCheckoutReturn();
})();
